<script setup>
/**
 * The card for an app that takes exactly one hook.
 *
 * Same shape as `Integration`, but the thing being switched on is a hook rather
 * than the app record, so connecting opens the hook form and disconnecting
 * hands the one existing hook back to the page that owns the confirm dialog.
 */
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { useBranding } from 'shared/composables/useBranding';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import SettingsStatusChip from 'dashboard/components-next/Settings/SettingsStatusChip.vue';

const props = defineProps({
  integrationId: {
    type: String,
    required: true,
  },
});

defineEmits(['add', 'delete']);

const { integration, hasConnectedHooks } = useIntegrationHook(
  props.integrationId
);

const { replaceInstallationName } = useBranding();
</script>

<template>
  <SettingsSection>
    <div class="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center">
      <div class="flex items-center justify-center flex-shrink-0 size-10">
        <img
          :src="`/dashboard/images/integrations/${integrationId}.png`"
          class="block max-w-full border rounded-md shadow-sm dark:hidden border-n-weak bg-n-alpha-3 dark:bg-n-alpha-2"
        />
        <img
          :src="`/dashboard/images/integrations/${integrationId}-dark.png`"
          class="hidden max-w-full border rounded-md shadow-sm dark:block border-n-weak bg-n-alpha-3 dark:bg-n-alpha-2"
        />
      </div>

      <div class="flex flex-col flex-1 min-w-0 gap-1">
        <div class="flex items-center gap-2 min-w-0">
          <h3 class="text-sm font-medium truncate text-n-slate-12">
            {{ integration.name }}
          </h3>
          <SettingsStatusChip
            :label="
              hasConnectedHooks
                ? $t('SETTINGS_KIT.CONNECTED')
                : $t('SETTINGS_KIT.READY_TO_USE')
            "
            :tone="hasConnectedHooks ? 'success' : 'neutral'"
          />
        </div>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ replaceInstallationName(integration.description) }}
        </p>
      </div>

      <div class="flex items-center flex-shrink-0">
        <Button
          v-if="hasConnectedHooks"
          size="sm"
          variant="faded"
          color="ruby"
          :label="$t('INTEGRATION_APPS.DISCONNECT.BUTTON_TEXT')"
          @click="$emit('delete', integration.hooks[0])"
        />
        <Button
          v-else
          size="sm"
          variant="faded"
          color="blue"
          :label="$t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')"
          @click="$emit('add')"
        />
      </div>
    </div>
  </SettingsSection>
</template>
